import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuidV4 } from "uuid";
import { firestore, storage, FieldValue } from "../../config/firebaseConfig";

// UTT reference data (Firestore): OEM minimum-wall documents and scan plans.
// New collections per the log-migration direction — no RTDB.

const unsubscribers = {};

async function uploadAttachment(file, folder) {
  const storagePath = `utt/${folder}/${uuidV4()}/${file.name}`;
  const snapshot = await storage.ref(storagePath).put(file);
  const downloadUrl = await snapshot.ref.getDownloadURL();
  return {
    name: file.name,
    size: file.size,
    contentType: file.type,
    storagePath,
    downloadUrl,
  };
}

export const fetchOemDocsAsync = createAsyncThunk(
  "uttReference/fetchOemDocs",
  async (_, thunkAPI) => {
    if (unsubscribers.oemDocs) return;
    unsubscribers.oemDocs = firestore.collection("oemMinWallDocs").onSnapshot(
      (snapshot) => {
        const docs = {};
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (!data.deleted) docs[doc.id] = data;
        });
        thunkAPI.dispatch(setOemDocs(docs));
      },
      (error) => {
        console.error("oemMinWallDocs listener error", error);
        thunkAPI.dispatch(setError(error.message));
      }
    );
  }
);

export const fetchScanPlansAsync = createAsyncThunk(
  "uttReference/fetchScanPlans",
  async (_, thunkAPI) => {
    if (unsubscribers.scanPlans) return;
    unsubscribers.scanPlans = firestore.collection("scanPlans").onSnapshot(
      (snapshot) => {
        const plans = {};
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (!data.deleted) plans[doc.id] = data;
        });
        thunkAPI.dispatch(setScanPlans(plans));
      },
      (error) => {
        console.error("scanPlans listener error", error);
        thunkAPI.dispatch(setError(error.message));
      }
    );
  }
);

export const cancelFetchUttReference = createAsyncThunk(
  "uttReference/cancel",
  async () => {
    Object.keys(unsubscribers).forEach((key) => {
      unsubscribers[key]();
      delete unsubscribers[key];
    });
  }
);

export const saveOemDocAsync = createAsyncThunk(
  "uttReference/saveOemDoc",
  async ({ id, file, user, ...values }, thunkAPI) => {
    try {
      const attachment = file
        ? await uploadAttachment(file, "oemDocs")
        : values.attachment || null;
      const stamp = {
        updatedAt: FieldValue.serverTimestamp(),
        updatedBy: { uid: user?.uid || "", name: user?.name || "" },
      };
      if (id) {
        await firestore
          .collection("oemMinWallDocs")
          .doc(id)
          .set({ ...values, attachment, ...stamp }, { merge: true });
        return id;
      }
      const ref = await firestore.collection("oemMinWallDocs").add({
        ...values,
        attachment,
        deleted: false,
        uploadedAt: FieldValue.serverTimestamp(),
        uploadedBy: { uid: user?.uid || "", name: user?.name || "" },
        ...stamp,
      });
      return ref.id;
    } catch (error) {
      console.error("saveOemDoc failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteOemDocAsync = createAsyncThunk(
  "uttReference/deleteOemDoc",
  async (id, thunkAPI) => {
    try {
      await firestore
        .collection("oemMinWallDocs")
        .doc(id)
        .set({ deleted: true }, { merge: true });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const saveScanPlanAsync = createAsyncThunk(
  "uttReference/saveScanPlan",
  async ({ id, diagramFile, user, ...values }, thunkAPI) => {
    try {
      const diagram = diagramFile
        ? await uploadAttachment(diagramFile, "scanPlanDiagrams")
        : values.diagram || null;
      const stamp = {
        updatedAt: FieldValue.serverTimestamp(),
        updatedBy: { uid: user?.uid || "", name: user?.name || "" },
      };
      if (id) {
        await firestore
          .collection("scanPlans")
          .doc(id)
          .set({ ...values, diagram, ...stamp }, { merge: true });
        return id;
      }
      const ref = await firestore.collection("scanPlans").add({
        status: "active",
        variants: {},
        assetAssignments: {},
        overrides: {},
        ...values,
        diagram,
        deleted: false,
        createdAt: FieldValue.serverTimestamp(),
        createdBy: { uid: user?.uid || "", name: user?.name || "" },
        ...stamp,
      });
      return ref.id;
    } catch (error) {
      console.error("saveScanPlan failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteScanPlanAsync = createAsyncThunk(
  "uttReference/deleteScanPlan",
  async (id, thunkAPI) => {
    try {
      await firestore
        .collection("scanPlans")
        .doc(id)
        .set({ deleted: true }, { merge: true });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const uttReferenceSlice = createSlice({
  name: "uttReference",
  initialState: {
    oemDocs: {},
    scanPlans: {},
    error: null,
  },
  reducers: {
    setOemDocs(state, action) {
      state.oemDocs = action.payload;
      state.error = null;
    },
    setScanPlans(state, action) {
      state.scanPlans = action.payload;
      state.error = null;
    },
    setError(state, action) {
      state.error = action.payload;
    },
  },
});

export const { setOemDocs, setScanPlans, setError } = uttReferenceSlice.actions;

export default uttReferenceSlice.reducer;
